import {POST} from '../../modules/fetch.ts';
import type {RepoBasePathInfo} from '../../types.ts';
import {pathEscapeSegments} from '../../utils/url.ts';
import type {ReplacementCandidate} from '../types/replacement_detection.ts';
import {NoChangesDetectedError, NotFoundError} from './errors.ts';

export type PostDetectReplacementsResponse = {
  candidates: ReplacementCandidate[];
};

const makeDetectReplacementsURL = (appSubUrl: string, {ownerName, repoName, baseRef}: RepoBasePathInfo) => {
  return `${appSubUrl}/${ownerName}/${repoName}/_upload_detect_replacements/${pathEscapeSegments(baseRef)}`;
};

const makeConfirmReplacementsURL = (appSubUrl: string, {ownerName, repoName, baseRef}: RepoBasePathInfo) => {
  return `${appSubUrl}/${ownerName}/${repoName}/_upload_confirm_replacements/${pathEscapeSegments(baseRef)}`;
};

export async function detectReplacements(appSubUrl: string, path: RepoBasePathInfo, token: string): Promise<PostDetectReplacementsResponse> {
  const resp = await POST(makeDetectReplacementsURL(appSubUrl, path), {
    data: {token},
  });
  if (resp.status === 404) {
    throw new NotFoundError();
  }
  if (!resp.ok) {
    throw new Error(`Request failed: ${resp.status}`);
  }
  return await resp.json();
}

export type ConfirmReplacementsFormData = {
  token: string;
  replacements: {
    source: string;
    dest: string;
  }[];
};

export type ConfirmReplacementsResponse = {
  redirect: string;
};

export async function confirmReplacements(appSubUrl: string, path: RepoBasePathInfo, formData: ConfirmReplacementsFormData) {
  const data = new FormData();
  data.append('token', formData.token);
  data.append('replacements', JSON.stringify(formData.replacements));

  const resp = await POST(makeConfirmReplacementsURL(appSubUrl, path), {
    data,
  });

  if (resp.status === 422) {
    const body = await resp.json() as {error_code?: string; message?: string};
    if (body.error_code === NoChangesDetectedError.code) {
      throw new NoChangesDetectedError(body.message);
    }
  }

  if (resp.status === 404) {
    throw new NotFoundError();
  }

  if (!resp.ok) {
    throw new Error(`Request failed: ${resp.status}`);
  }

  if (resp.redirected) {
    return {redirect: resp.url};
  }

  const location = resp.headers.get('Location');
  if (location) {
    return {redirect: location};
  }
  throw new Error('Confirm succeeded but no redirect was provided');
}
